import React, { useState, useEffect } from 'react';
import {
  Search, CheckCircle, XCircle, Clock, Eye, X, AlertCircle,
  ChevronLeft, ChevronRight, Package, RotateCw, FileText, User, Calendar
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

export default function ReturnRequestList() {
  const [returnRequests, setReturnRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [approving, setApproving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const userDataString = localStorage.getItem('user');
    if (userDataString) {
      try {
        const user = JSON.parse(userDataString);
        setCurrentUser(user);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    fetchReturnRequests();
  }, []);

  useEffect(() => {
    filterRequests();
  }, [searchTerm, statusFilter, returnRequests]);

  const fetchReturnRequests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest`);
      if (!res.ok) {
        setReturnRequests([]);
        setFilteredRequests([]);
        return;
      }
      const data = await res.json();
      setReturnRequests(Array.isArray(data) ? data : []);
      setFilteredRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching return requests:', error);
      setReturnRequests([]);
      setFilteredRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    let filtered = [...returnRequests];

    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.itemCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.mrHeaderId?.toString().includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === parseInt(statusFilter));
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      '-1': { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      '0': { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
      '1': { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle }
    };
    const badge = badges[String(status)] || badges['0'];
    const Icon = badge.icon;
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${badge.color} border inline-flex items-center gap-1.5`}>
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusCount = (status) => {
    return returnRequests.filter(req => req.status === status).length;
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  // ============================================================
  // ✅ HANDLE APPROVE — calls approve endpoint THEN update-qty
  // ============================================================
  const handleApprove = async (request) => {
    if (approving) return;

    const result = await Swal.fire({
      title: 'Approve Return Request?',
      html: `
        <div class="text-left">
          <p class="mb-2"><strong>Item:</strong> ${request.itemCode}</p>
          <p class="mb-2"><strong>Quantity:</strong> ${request.requestedReturnQty}</p>
          <p class="mb-2"><strong>Requested By:</strong> ${request.requestedByName}</p>
          <p class="mb-2"><strong>Reason:</strong> ${request.returnReason || 'N/A'}</p>
          <p class="mt-4 text-green-600 font-semibold">✅ This will return items to inventory</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setApproving(true);

    try {
      // ========================================
      // STEP 1 — Approve the return request
      // ========================================
      const approveRes = await fetch(`${API_BASE_URL}api/ReturnRequest/approve/${request.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approvedBy: currentUser?.userId || 1,
          approvedByName: currentUser
            ? `${currentUser.firstName} ${currentUser.lastName}`
            : 'Admin'
        })
      });

      if (!approveRes.ok) {
        const errData = await approveRes.json();
        throw new Error(errData.message || 'Failed to approve return request');
      }

      // ========================================
      // STEP 2 — Use /{id}/return endpoint
      // ✅ Quantity LANG — backend mismo ang bahala
      // ❌ Wala nang PUT, wala nang update-qty
      // ========================================

      // Get the inventoryDataId from the return request
      // If request has inventoryDataId, use it directly
      // Otherwise, find the record by itemCode + warehouseCode
      // ========================================
      // STEP 2 — GET then PUT, quantity LANG
      // ❌ availableQty — HINDI TINATOUCHED
      // ========================================
      try {
        let inventoryId = request.inventoryDataId;

        if (!inventoryId) {
          const invRes = await fetch(
            `${API_BASE_URL}api/InventoryData?itemCode=${encodeURIComponent(request.itemCode)}`
          );
          if (invRes.ok) {
            const records = await invRes.json();
            const list = Array.isArray(records) ? records : [];
            const matched = list.find(r => r.warehouseCode === request.warehouseCode) || list[0];
            inventoryId = matched?.id;
          }
        }

        if (inventoryId) {
          // GET current record
          const getRes = await fetch(`${API_BASE_URL}api/InventoryData/${inventoryId}`);
          if (getRes.ok) {
            const rec = await getRes.json();

            // PUT — quantity LANG ang babaguhin, availableQty same value pa rin
            await fetch(`${API_BASE_URL}api/InventoryData/${inventoryId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...rec,
                quantity: (Number(rec.quantity) || 0) + Number(request.requestedReturnQty),
                availableQty: rec.availableQty,  // ✅ SAME VALUE — hindi binabago
                lastUpdated: new Date().toISOString(),
              })
            });
          }
        }
      } catch (invErr) {
        console.warn('⚠️ Inventory update failed:', invErr);
      }
      await Swal.fire({
        icon: 'success',
        title: 'Approved!',
        html: `
          <p>Return request approved successfully!</p>
          <p class="mt-2 text-green-600 font-semibold">✅ Inventory updated for <strong>${request.itemCode}</strong></p>
          <p class="text-sm text-gray-500 mt-1">+${request.requestedReturnQty} added to quantity only</p>
        `,
        timer: 2500,
        confirmButtonColor: '#10b981'
      });

      fetchReturnRequests();
      setShowModal(false);

    } catch (error) {
      console.error('Error approving request:', error);
      Swal.fire({
        icon: 'error',
        title: 'Approval Failed',
        text: error.message || 'Could not approve return request',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (request) => {
    const { value: reason } = await Swal.fire({
      title: 'Reject Return Request?',
      input: 'textarea',
      inputLabel: 'Rejection Reason',
      inputPlaceholder: 'Enter reason for rejection...',
      inputAttributes: { 'aria-label': 'Rejection reason' },
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value) return 'Please provide a reason for rejection';
      }
    });

    if (!reason) return;

    try {
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest/reject/${request.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejectedBy: currentUser?.userId || 1,
          rejectedByName: currentUser
            ? `${currentUser.firstName} ${currentUser.lastName}`
            : 'Admin',
          rejectionReason: reason
        })
      });

      if (!res.ok) throw new Error('Failed to reject');

      await Swal.fire({
        icon: 'success',
        title: 'Rejected!',
        text: 'Return request has been rejected',
        timer: 2000,
        confirmButtonColor: '#10b981'
      });

      fetchReturnRequests();
      setShowModal(false);
    } catch (error) {
      console.error('Error rejecting request:', error);
      Swal.fire({
        icon: 'error',
        title: 'Rejection Failed',
        text: 'Could not reject return request',
        confirmButtonColor: '#dc2626'
      });
    }
  };
  const handleDeleteRequest = async (request) => {
    const result = await Swal.fire({
      title: 'Delete Return Request?',
      html: `<p><strong>${request.itemCode}</strong> — Qty: ${request.requestedReturnQty}</p><p class="text-red-600 mt-2 font-semibold">⚠️ This cannot be undone!</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest/${request.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      await Swal.fire({ icon: 'success', title: 'Deleted!', timer: 2000, showConfirmButton: false });
      fetchReturnRequests();
      setShowModal(false);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: error.message, confirmButtonColor: '#dc2626' });
    }
  };
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
                <RotateCw className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Return Requests</h1>
                <p className="text-gray-500 mt-1">Review and approve item return requests</p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-5 hover:shadow-md transition-all">
              <Clock className="w-10 h-10 text-amber-500 mb-2" />
              <p className="text-amber-700 text-xs font-semibold uppercase tracking-wide mb-1">Pending</p>
              <p className="text-3xl font-bold text-amber-900">{getStatusCount(0)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5 hover:shadow-md transition-all">
              <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
              <p className="text-green-700 text-xs font-semibold uppercase tracking-wide mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-900">{getStatusCount(1)}</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-5 hover:shadow-md transition-all">
              <XCircle className="w-10 h-10 text-red-500 mb-2" />
              <p className="text-red-700 text-xs font-semibold uppercase tracking-wide mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-900">{getStatusCount(-1)}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by Item Code, Requester Name, MR ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[180px] transition-all"
            >
              <option value="all">All Status</option>
              <option value="0">Pending</option>
              <option value="1">Approved</option>
              <option value="-1">Rejected</option>
            </select>
          </div>
        </div>

        {/* Return Requests Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                <p className="text-gray-600 font-medium">Loading Return Requests...</p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-20">
              <RotateCw className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium">No return requests found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">MR ID</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Item Code</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Description</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Return Qty</th>
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Requested By</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {currentRequests.map((req) => (
                      <tr key={req.id} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{formatDate(req.requestedDate)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <FileText className="w-3.5 h-3.5" />
                            {req.mrHeaderId}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono font-bold text-gray-900">{req.itemCode}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-700">{req.itemDescription?.substring(0, 40)}...</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200">
                            <Package className="w-3.5 h-3.5" />
                            {req.requestedReturnQty}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-gray-900">{req.requestedByName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-center">
                            {getStatusBadge(req.status)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleViewRequest(req)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>

                            {req.status === 0 && (
                              <>
                                <button
                                  onClick={() => handleApprove(req)}
                                  disabled={approving}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  {approving ? 'Processing...' : 'Approve'}
                                </button>
                                <button
                                  onClick={() => handleReject(req)}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Reject
                                </button>
                              </>
                            )}
                            {currentUser?.positionId === 'SYSTEM ADMIN' && (
                              <button
                                onClick={() => handleDeleteRequest(req)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-xl transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                              >
                                <X className="w-4 h-4" />
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-bold text-gray-900">{startIndex + 1}</span> to{' '}
                    <span className="font-bold text-gray-900">{Math.min(endIndex, filteredRequests.length)}</span> of{' '}
                    <span className="font-bold text-gray-900">{filteredRequests.length}</span> requests
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                          return (
                            <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-md' : 'border border-gray-200 hover:bg-gray-100'}`}>
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* View Request Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <RotateCw className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Return Request Details</h3>
                    <p className="text-blue-100 text-sm mt-1">Request ID: {selectedRequest.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-white/10 hover:bg-white/20 rounded-xl p-3 transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">MR Header ID</p>
                    <p className="text-lg font-bold text-gray-900">{selectedRequest.mrHeaderId}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Status</p>
                    {getStatusBadge(selectedRequest.status)}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Item Details</p>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      <span className="font-mono">{selectedRequest.itemCode}</span>
                    </p>
                    <p className="text-sm text-gray-700 mb-3">{selectedRequest.itemDescription}</p>
                    <div className="flex items-center gap-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-bold">
                        Return Qty: {selectedRequest.requestedReturnQty}
                      </span>
                      <span className="text-sm text-gray-600">
                        Warehouse: {selectedRequest.warehouseCode || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Return Reason</p>
                  <p className="text-sm text-gray-700 bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
                    {selectedRequest.returnReason || 'No reason provided'}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Request Information</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Requested By:</p>
                      <p className="font-semibold text-gray-900">{selectedRequest.requestedByName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Requested Date:</p>
                      <p className="font-semibold text-gray-900">{formatDate(selectedRequest.requestedDate)}</p>
                    </div>
                  </div>
                </div>

                {selectedRequest.status !== 0 && (
                  <div className="border-t pt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      {selectedRequest.status === 1 ? 'Approval' : 'Rejection'} Information
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">{selectedRequest.status === 1 ? 'Approved' : 'Rejected'} By:</p>
                        <p className="font-semibold text-gray-900">{selectedRequest.approvedByName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Date:</p>
                        <p className="font-semibold text-gray-900">{formatDate(selectedRequest.approvedDate)}</p>
                      </div>
                    </div>
                    {selectedRequest.status === -1 && selectedRequest.rejectionReason && (
                      <div className="mt-3">
                        <p className="text-gray-500">Rejection Reason:</p>
                        <p className="text-sm text-red-700 bg-red-50 border-l-4 border-red-500 p-3 rounded mt-1">
                          {selectedRequest.rejectionReason}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t bg-gray-50 px-8 py-5 flex items-center justify-between rounded-b-2xl">
              {selectedRequest.status === 0 ? (
                <>
                  <button
                    onClick={() => handleReject(selectedRequest)}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2"
                  >
                    <XCircle className="w-5 h-5" />
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedRequest)}
                    disabled={approving}
                    className="px-8 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all shadow-lg flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {approving ? 'Processing...' : 'Approve Request'}
                  </button>
                </>
              ) : (
                <div className="flex items-center justify-between w-full">
                  {currentUser?.positionId === 'SYSTEM ADMIN' && (
                    <button
                      onClick={() => handleDeleteRequest(selectedRequest)}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-md flex items-center gap-2"
                    >
                      <X className="w-5 h-5" />
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => setShowModal(false)}
                    className="ml-auto px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-semibold transition-all shadow-md"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}